import { getClientEnv } from "@shared/env";
import { Alert, Button, Card, Checkbox, Form, Input, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import {
	useNotificationChannels,
	useRotateTelegramBotToken,
	useSaveNotificationChannels,
	useTelegramBotTokenStatus,
} from "../hooks/useNotifications";
import {
	CHANNEL_LABELS,
	NOTIFICATION_TYPE_LABELS,
	type NotificationChannel,
	type NotificationChannelsMap,
	notificationTypeLabel,
	type TelegramBotTokenStatus,
} from "../types";

const { Title, Paragraph, Text } = Typography;

/**
 * Har xabar turi uchun kanal (TT §316: «har bir xabar turi uchun kanal
 * alohida sozlanadi»).
 *
 * ⚠️ **SMS va elektron pochta YO'Q** — TT §316 qat'iy (spec §7.2), shuning
 * uchun ustunlar ro'yxati uchtadan iborat va u yerdan kengaytirilmaydi.
 *
 * ⚠️ Bu ekran YANGI endpoint ishlatmaydi:
 * `PATCH /settings/notification_channels`. Sozlamaning yagona yozuvchisi —
 * `settings` resursi; ikkinchi yozuvchi haqiqat manbaini ikkiga bo'lardi.
 */
const KANALLAR: NotificationChannel[] = ["push", "telegram", "panel"];

interface QatorType {
	type: string;
}

/**
 * `GET /settings` `value` ni `unknown` qaytaradi (`jsonb`). Buzuq yoki
 * kutilmagan shakl butun ekranni yiqitmasligi kerak — bo'sh xarita
 * qaytariladi.
 *
 * ⚠️ Bu yerda standart bilan BIRLASHTIRILMAYDI va bu ataylab:
 * `settings.repository.ts` ning `list()` i allaqachon AMALDAGI xaritani
 * qaytaradi (`mergeNotificationChannels`, `packages/core/src/settings.ts`) —
 * `enqueueNotification` bilan bitta qoida. Standartlarni bu yerda
 * takrorlash ikkinchi haqiqat manbai bo'lardi va aynan panel/runtime
 * ajralishini qaytarardi.
 */
function xaritaOqi(value: unknown): NotificationChannelsMap {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return {};
	}

	return value as NotificationChannelsMap;
}

/**
 * ⚠️ **FAQAT `source === "none"` bo'lganda zaxira sifatida** — token
 * UMUMAN sozlanmagan, ya'ni bu ekranning token-rotatsiya funksiyasi
 * mavjud bo'lishidan OLDINGI, sof-env konfiguratsiya. `source === "env"`
 * bo'lganda (token bor, lekin hali panel orqali aylanmagan) bu zaxira
 * ATAYLAB ISHLATILMAYDI: o'sha token Telegram'ning `getMe`si bilan
 * HECH QACHON tasdiqlanmagan, ya'ni `VITE_TELEGRAM_BOT_USERNAME`
 * eskirgan/mos kelmaydigan bo'lishi mumkin — uni tasdiqlangandek
 * ko'rsatish aynan shu funksiya oldini olmoqchi bo'lgan DRIFT'ning
 * o'zi. Token va manzil endi Telegram'ning `getMe` metodi orqali BIR
 * TRANZAKSIYADA aniqlanadi (`rotateTelegramBotToken()`), qo'lda
 * kiritish esa OLIB TASHLANDI.
 */
function envBotUsername(): string | undefined {
	const raw = getClientEnv().VITE_TELEGRAM_BOT_USERNAME;
	return raw?.replace(/^@/, "");
}

/** Backend `settings.repository.ts`ning `rotateTelegramBotToken()` shakli bilan AYNAN bir xil. */
const TELEGRAM_TOKEN_PATTERN = /^\d{5,15}:[A-Za-z0-9_-]{30,45}$/;

/**
 * Holat matni — `source` uchtasi uchun uchta boshqa xabar (spec: token
 * UI). `env` holatida ham `last4` ko'rsatiladi: `settings:read` ruxsati
 * allaqachon yetarlicha cheklangan (Xodim roliga hech qachon berilmaydi,
 * CLAUDE.md), 4 belgi 35 belgili sirni tiklamaydi.
 */
function tokenStatusText(status: TelegramBotTokenStatus, t: TFunction<"notifications">): string {
	if (status.source === "db") {
		return t("channels.telegramTokenStatusDb", {
			last4: status.last4,
			updatedAt: status.updatedAt ? new Date(status.updatedAt).toLocaleString("uz-UZ") : "",
		});
	}

	if (status.source === "env") {
		return t("channels.telegramTokenStatusEnv", { last4: status.last4 });
	}

	return t("channels.telegramTokenStatusNone");
}

export function NotificationChannelsPage() {
	const { t } = useTranslation("notifications");
	const { setting, isLoading, isError } = useNotificationChannels();
	const save = useSaveNotificationChannels();
	const [qoralama, setQoralama] = useState<NotificationChannelsMap>({});

	/**
	 * ⚠️ Bog'liqlik `setting` (server obyekti), undan chiqarilgan xarita
	 * EMAS. Har renderda `?? {}` bilan yangi obyekt yasalsa, `useEffect`
	 * cheksiz siklga tushardi (`setQoralama` → render → yangi `{}` →
	 * effekt → ...) — `SettingsPage` da aynan shu regressiya o'lchangan.
	 * Yuklanayotgan holatda qoralamaga umuman tegilmaydi.
	 */
	useEffect(() => {
		if (setting) {
			setQoralama(xaritaOqi(setting.value));
		}
	}, [setting]);

	const { data: tokenStatus, isLoading: isTokenStatusLoading } = useTelegramBotTokenStatus();
	const rotateToken = useRotateTelegramBotToken();
	const [tokenDraft, setTokenDraft] = useState("");

	/**
	 * ⚠️ Zaxira FAQAT `source === "none"` da — `envBotUsername()`
	 * izohiga qarang. `source === "env"` bo'lsa (token bor, tasdiqlanmagan)
	 * `null` qoladi: tasdiqlanmagan manzilni tasdiqlangandek ko'rsatish
	 * aynan shu funksiya oldini olmoqchi bo'lgan drift.
	 */
	const botUsername =
		tokenStatus?.username ??
		(tokenStatus?.source === "none" ? envBotUsername() : undefined) ??
		null;

	const tokenError =
		tokenDraft !== "" && !TELEGRAM_TOKEN_PATTERN.test(tokenDraft)
			? t("channels.telegramTokenInvalid")
			: null;

	function handleRotateToken() {
		rotateToken.mutate(tokenDraft, { onSuccess: () => setTokenDraft("") });
	}

	const turlar = Object.keys(NOTIFICATION_TYPE_LABELS);

	function almashtir(type: string, channel: NotificationChannel, tanlandi: boolean) {
		setQoralama((oldingi) => {
			const joriy = oldingi[type] ?? [];
			const yangi = tanlandi ? [...joriy, channel] : joriy.filter((element) => element !== channel);

			return { ...oldingi, [type]: yangi };
		});
	}

	const columns: ColumnsType<QatorType> = [
		{
			title: t("channels.typeColumn"),
			dataIndex: "type",
			key: "type",
			render: (type: string) => (
				<div>
					<div>{t(`types.${type}`, { defaultValue: notificationTypeLabel(type) })}</div>
					<div className="text-gray-400 text-xs">{type}</div>
				</div>
			),
		},
		...KANALLAR.map((channel) => ({
			title: t(`channelLabels.${channel}`, { defaultValue: CHANNEL_LABELS[channel] }),
			key: channel,
			width: 120,
			render: (_: unknown, row: QatorType) => (
				<Checkbox
					checked={(qoralama[row.type] ?? []).includes(channel)}
					aria-label={`${row.type}:${channel}`}
					onChange={(event) => almashtir(row.type, channel, event.target.checked)}
				/>
			),
		})),
	];

	return (
		<div>
			<Title level={3}>{t("channels.title")}</Title>
			<Paragraph type="secondary">{t("channels.description")}</Paragraph>

			<Card className="mb-4">
				<Title level={5}>{t("channels.telegramTokenTitle")}</Title>
				<Paragraph type="secondary">{t("channels.telegramTokenDescription")}</Paragraph>

				{tokenStatus && (
					<Alert
						className="mb-4"
						type={tokenStatus.configured ? "success" : "warning"}
						showIcon
						description={tokenStatusText(tokenStatus, t)}
					/>
				)}

				{/*
				 * ⚠️ FAQAT O'QISH UCHUN — botning manzili endi tokendan
				 * avtomatik aniqlanadi (`getMe`), qo'lda tahrirlanmaydi.
				 * Bo'sh/aniqlanmagan holatda havola ko'rsatilmaydi
				 * ("@undefined" bilan chalg'itmaslik uchun) — o'rniga
				 * tokenni kiritish/qayta kiritish ko'rsatmasi chiqadi.
				 */}
				<Paragraph>
					<Text strong>{t("channels.telegramBotLabel")}:</Text>{" "}
					{botUsername ? (
						<>
							<Text code>@{botUsername}</Text> —{" "}
							<a href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer">
								https://t.me/{botUsername}
							</a>
						</>
					) : (
						<Text type="secondary">{t("channels.telegramUsernameUnknown")}</Text>
					)}
				</Paragraph>

				<Form layout="vertical">
					<Form.Item
						label={t("channels.telegramTokenLabel")}
						validateStatus={tokenError ? "error" : undefined}
						help={tokenError ?? t("channels.telegramTokenExtra")}
					>
						{/*
						 * ⚠️ HECH QACHON oldindan to'ldirilmaydi — bu maydon FAQAT
						 * yangi qiymat kiritish uchun (yozish-faqat). `useTelegramBotTokenStatus`
						 * xom tokenni umuman qaytarmaydi, ya'ni prefill qilishning
						 * texnik imkoni ham yo'q.
						 */}
						<Input.Password
							value={tokenDraft}
							maxLength={100}
							placeholder={t("channels.telegramTokenPlaceholder")}
							autoComplete="new-password"
							onChange={(event) => setTokenDraft(event.target.value.trim())}
						/>
					</Form.Item>

					<PermissionGate resource="settings" action="update">
						<Button
							type="primary"
							loading={rotateToken.isPending}
							disabled={isTokenStatusLoading || tokenDraft === "" || Boolean(tokenError)}
							onClick={handleRotateToken}
						>
							{t("channels.telegramTokenSave")}
						</Button>
					</PermissionGate>
				</Form>
			</Card>

			{isError && (
				<Alert
					className="mb-4"
					type="error"
					showIcon
					title={t("channels.loadError.title")}
					description={t("channels.loadError.description")}
				/>
			)}

			<Alert
				className="mb-4"
				type="info"
				showIcon
				// ⚠️ Bu matn `enqueueNotification` ning haqiqiy xulqini tasvirlaydi:
				// bo'sh ro'yxat `["panel"]` ga tushadi, ya'ni xabar YO'QOLMAYDI —
				// u faqat panelda qoladi.
				title={t("channels.panelOnlyNotice.title")}
				description={t("channels.panelOnlyNotice.description")}
			/>

			<Card>
				<Table<QatorType>
					columns={columns}
					dataSource={turlar.map((type) => ({ type }))}
					rowKey="type"
					loading={isLoading}
					pagination={false}
					scroll={{ x: "max-content" }}
				/>

				<PermissionGate resource="settings" action="update">
					{/*
					 * ⚠️ **`disabled` — bo'sh xaritani ustidan yozishning qorovuli.**
					 * `qoralama` `setting` kelmaguncha `{}` bo'lib turadi va
					 * `PATCH /settings/notification_channels` bo'sh obyektni QABUL
					 * qiladi (`z.record` da `min` yo'q). Yuklanish tugamasdan yoki
					 * `GET /settings` xato bilan tugagandan keyin bosilgan «Saqlash»
					 * butun xaritani `{}` bilan almashtirardi — administrator
					 * ataylab o'chirgan turlar jimgina standart kanallariga
					 * qaytardi (`enqueueNotification` yo'q kalit uchun standartni
					 * oladi), toast esa yashil «saqlandi» deb chiqardi.
					 */}
					<Button
						className="mt-4"
						type="primary"
						loading={save.isPending}
						disabled={isLoading || isError || !setting}
						onClick={() => save.mutate(qoralama)}
					>
						{t("channels.save")}
					</Button>
				</PermissionGate>
			</Card>
		</div>
	);
}
